import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

interface IProps {
  items: { [key: string]: unknown }[];
  headers: { key: string; label: string }[];
  fileName: string;
  format: 'xlsx' | 'csv';
  widthCools?: { wch: number }[];
  fileNameWithDate?: boolean;
  sendEmailBase64?: boolean;
  handleUploadFile?: (data: {
    fileContent: unknown;
    fileName: string;
    description: string;
  }) => void;
  automaticWidth?: boolean;
}

/**
 * The `export function GenerateXLSX()` is a TypeScript function that generates an Excel file based on
the provided parameters. Here is a breakdown of the function's parameters:
  @param items is an `array` of objects that contains the data to be exported to the Excel file. Each
  object in the array represents a row in the Excel file, and the properties of the object represent
  the values in each column of the row.
  @param headers parameter is an `array` of objects that define the columns in the Excel file. Each
  object in the array represents a column and has two properties: `key` and `label`.
  @param fileName parameter is a `string` that represents the name of the Excel file that will be
  generated. It is used to specify the name of the file when saving it.
  @param fileNameWithDate parameter is a `boolean` flag that determines whether or not to include the
  current date in the generated file name. If `fileNameWithDate` is set to `true`, the current date
  in the format "DD-MM-YYYY" will be appended to the `fileName` parameter. If `fileNameWithDate` is
  set to `false`, the current date will not be included in the file name.
  @param format parameter in the `GenerateXLSX` function is used to specify the format of the
  generated Excel file. It is an optional parameter with a default value of `'xlsx'`.
  @param  widthCools parameter is an optional parameter in the `GenerateXLSX` function. It is used to
  specify the width of the columns in the generated Excel file.
  @param  sendEmailBase64 parameter is a `boolean` flag that determines whether or not to send the
  generated Excel file as an email attachment in base64 format.
  @param  handleUploadFileService function is a callback function that is responsible for handling the
  upload of the generated file. It takes in an object parameter with the following properties:
   `fileContent, fileName and description`
*/

export function GenerateXLSX({
  items,
  headers,
  fileName,
  fileNameWithDate = true,
  format = 'xlsx',
  widthCools,
  sendEmailBase64,
  handleUploadFile,
  automaticWidth
}: IProps) {
  const fileNameFormated = `${fileName}_${
    fileNameWithDate ? dayjs().format('DD_MM_YYYY') : ''
  }.${format}`;

  let itemsExport: unknown[] = [];
  if (items?.length) {
    itemsExport = items?.map((item) => {
      let itemFormated = {};
      headers?.forEach((itemHeader) => {
        if (item[itemHeader.key]) {
          itemFormated = {
            ...itemFormated,
            [itemHeader.label]: item[itemHeader.key]
          };
        } else {
          itemFormated = {
            ...itemFormated,
            [itemHeader.label]: ''
          };
        }
      });
      return itemFormated;
    });
  }

  const workbook = XLSX.utils.book_new();
  const itemsBook = XLSX.utils.json_to_sheet(itemsExport);

  if (automaticWidth) {
    const keys = Object.keys(itemsExport[0]);
    widthCools = keys.map((a: any) => ({
      wch: a.length
    }));
  }

  if (widthCools?.length) itemsBook['!cols'] = widthCools;

  XLSX.utils.book_append_sheet(workbook, itemsBook, fileName);

  if (sendEmailBase64) {
    const wbout: unknown = XLSX.write(workbook, {
      type: 'base64',
      bookType: format
    });
    handleUploadFile({
      fileContent: wbout,
      fileName: fileNameFormated,
      description: `Exportación de ${fileName} en formato ${format}`
    });
    return;
  }
  XLSX.writeFile(workbook, fileNameFormated);
}
